import { FIELD_LABEL_CLASS, FIELD_LABEL_LINE_CLASS } from 'components/GridItemList';
import { useInRecordSection } from 'components/section-edit/SectionEdit';
import {
    CreateDialogField,
    useInCreateDialogSections,
    useStarredInRequired,
} from 'components/create-dialog/CreateDialogRequired';
import {
    useHighlightedFieldsDispatch,
    useSectionActions,
    useSectionTitle,
} from 'components/section-menu/SectionActionsContext';
import { useDraftField } from 'components/drafts/DraftSaveMode';
import { Field, FieldError, FieldLabel } from 'components/ui/field';
import React, { createContext, type RefObject, useContext, useEffect, useId, useLayoutEffect, useRef } from 'react';
import { useGetUserDataQuery } from 'store/api/user';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { designInputsOf, useIsNewDesignOf } from 'utils/impersonationDesign';

type Props = {
    name: string;
    children: React.ReactElement | React.ReactElement[];
    rightLabelComponent?: React.ReactNode;
    bottomRightLabelComponent?: React.ReactNode;
    hasErrors: boolean;
    /** Rendered under the control. Inputs that draw their own message leave this out. */
    error?: { message?: string };
    label?: string;
    shouldTranslateLabel?: boolean;
    tooltip?: JSX.Element;
    required?: boolean;
    isEmpty?: boolean;
    className?: string;
    gap?: number;
    readonly?: boolean;
    noReadOnlyDivider?: boolean;
    noFullWidthInputWrapper?: boolean;
};

const FieldLabelsBesideContext = React.createContext(false);

/**
 * Opts a form in to fields laid out with the label beside the control, in a column of its own, as the new design's
 * send e-mail sheet does. Off unless provided; the fields of every other form keep their label above.
 */
export const FieldLabelsBeside = FieldLabelsBesideContext.Provider;

/** Whether the fields here lay their label beside their control (see {@link FieldLabelsBeside}). */
export const useFieldLabelsBeside = () => useContext(FieldLabelsBesideContext);

/** One step of the gap scale, so `gap={1}` stays the 8px it has always been. */
const GAP_STEP_PX = 8;
/** The step in the create dialog, whose fields sit closer under their labels. */
const CREATE_DIALOG_GAP_STEP_PX = 4;

/**
 * A highlighted field tints its whole box and repeats the warning on whichever control it wraps —
 * the bordered surfaces are addressed by their kit slot, since the wrapper cannot know which one
 * of them the field renders.
 */
const HIGHLIGHT_CLASSES = [
    'bg-warning-light rounded-sm ring-4 ring-warning-light',
    '[&_[data-slot=input]]:border-warning [&_[data-slot=input-group]]:border-warning',
    '[&_[data-slot=textarea]]:border-warning [&_[data-slot=combobox-chips]]:border-warning',
    '[&_[data-slot=input]]:shadow-[-3px_0_0_0_var(--color-warning)]',
    '[&_[data-slot=input-group]]:shadow-[-3px_0_0_0_var(--color-warning)]',
    '[&_[data-slot=combobox-chips]]:shadow-[-3px_0_0_0_var(--color-warning)]',
].join(' ');

// In a record section a field is the read view's cell: the label, 4px, then the control as the value's 36px box,
// its text the read value's 14px medium. A bordered kit control hangs out of the cell on the left by the fields
// grid's `--field-hang`, the width of its border and padding, so its text starts at the cell's edge. Any other
// control, such as a radio group or an address, has no box and stays where it is.
const SECTION_CONTROL_CLASS = [
    'mt-1',
    '[&>:is([data-slot=input],[data-slot=input-group],[data-slot=textarea],[data-slot=combobox-chips])]:-ml-[var(--field-hang,0px)]',
    '[&>:is([data-slot=input],[data-slot=input-group],[data-slot=textarea],[data-slot=combobox-chips])]:w-[calc(100%+var(--field-hang,0px))]',
    '[&>:is([data-slot=input],[data-slot=input-group],[data-slot=textarea],[data-slot=combobox-chips])]:text-sm',
    '[&>:is([data-slot=input],[data-slot=input-group],[data-slot=textarea],[data-slot=combobox-chips])]:font-medium',
    '[&_[data-slot=input-group-control]]:text-sm',
].join(' ');
// A read-only field there has no control: its text takes the read value's place in the box, which ends 11px short
// of the cell.
const SECTION_READONLY_CLASS = 'mt-1 min-h-9 py-2 pr-[11px] text-sm font-medium';
// What sits right of a label there takes what the label leaves of the line, an info icon's width at least, and is
// cut there on one line: the label wraps where the read view's does, and nothing beside it adds a line.
const SECTION_RIGHT_LABEL_CLASS = 'flex min-w-5 flex-1 overflow-x-clip whitespace-nowrap [&>:first-child]:ml-auto';

/**
 * Makes the field's error the description of its control, so it is read with the field and not as an alert of its
 * own. The control is the caller's: the wrapper finds it under itself as the element the input marks
 * `aria-invalid`. It runs after every render while the error shows, so a control drawn anew is tied again; its id
 * is added to whatever the control is described by already, and taken back alone. An input that marks no control
 * (a radio group, a file drop) leaves the message tied to nothing: it is then an alert, as a `FieldError` on its
 * own is. A layout effect, so that role is on the message before it is first exposed.
 */
function useErrorDescribesControl(field: RefObject<HTMLElement | null>, errorId: string, shown: boolean) {
    useLayoutEffect(() => {
        if (!shown) return undefined;
        const control = field.current?.querySelector('[aria-invalid="true"]');
        const message = document.getElementById(errorId);
        if (!control) {
            message?.setAttribute('role', 'alert');
            return undefined;
        }
        message?.removeAttribute('role');
        const describedBy = () => (control.getAttribute('aria-describedby') ?? '').split(' ').filter(Boolean);
        if (!describedBy().includes(errorId)) {
            control.setAttribute('aria-describedby', [...describedBy(), errorId].join(' '));
        }
        return () => {
            const rest = describedBy().filter((id) => id !== errorId);
            if (rest.length > 0) control.setAttribute('aria-describedby', rest.join(' '));
            else control.removeAttribute('aria-describedby');
        };
    });
}

/**
 * A field of a form: its label, its control and its error, which describes the control. In the create dialog's
 * sections it is drawn in the Required section while `required` is true, and it holds its control in one block
 * there, which the Required section lines up across a row.
 */
const InputWrapper = ({
    name,
    label,
    shouldTranslateLabel = true,
    tooltip,
    required,
    isEmpty,
    children,
    rightLabelComponent,
    bottomRightLabelComponent,
    hasErrors,
    error,
    gap = 1,
    readonly = false,
    noReadOnlyDivider = false,
    noFullWidthInputWrapper = false,
    className,
}: Props) => {
    const { data: userData } = useGetUserDataQuery();
    const { hideEmptyFields } = useSectionActions();
    const inSection = useInRecordSection();
    const labelBeside = useFieldLabelsBeside();
    // Each design keeps its own choice, and the field follows the one of the design shown: while impersonating,
    // the design chosen in this browser when there is one.
    const isNewDesign = useIsNewDesignOf(designInputsOf(userData));
    const shouldHighlight = isNewDesign
        ? userData?.preferences?.newDesignHighlightRequiredFields === true
        : userData?.preferences?.highlightRequiredFields;
    const shouldHide = hideEmptyFields && !required && isEmpty === true;

    const isHighlighted = !!(required && isEmpty && shouldHighlight);

    const sectionTitle = useSectionTitle();
    const { registerHighlightedField, unregisterHighlightedField } = useHighlightedFieldsDispatch();
    const inCreateDialogSections = useInCreateDialogSections();
    // Drawn only: a block of the create dialog that holds the field in Required stars it, and no rule follows.
    const starredByForm = useContext(StarredContext);
    const isStarred = useStarredInRequired() || starredByForm || !!required;
    const fieldRef = useRef<HTMLDivElement>(null);
    // A draft save names the fields that carry the asterisk and are left empty.
    useDraftField(label && (shouldTranslateLabel ? t(label) : label), !readonly && isStarred, isEmpty, fieldRef);
    const errorId = useId();
    useErrorDescribesControl(fieldRef, errorId, !!error?.message);

    useEffect(() => {
        if (isHighlighted && sectionTitle) {
            registerHighlightedField(name, sectionTitle);
        } else {
            unregisterHighlightedField(name);
        }
        return () => unregisterHighlightedField(name);
    }, [isHighlighted, name, sectionTitle, registerHighlightedField, unregisterHighlightedField]);

    const gapStepPx = inCreateDialogSections ? CREATE_DIALOG_GAP_STEP_PX : GAP_STEP_PX;

    const control = (
        <>
            {inSection ? (
                <div className={readonly ? SECTION_READONLY_CLASS : cn(SECTION_CONTROL_CLASS, !label && 'mt-0')}>
                    {children}
                </div>
            ) : (
                <div
                    className={cn(labelBeside && 'sm:col-start-2')}
                    style={label !== undefined && !labelBeside ? { paddingTop: gap * gapStepPx } : undefined}
                >
                    {children}
                </div>
            )}
            {/* No alert role while the message is tied to its control: a form's worth of alerts would talk over
                each other, and the form's own summary is the alert. */}
            {error ? (
                <FieldError
                    id={errorId}
                    role={undefined}
                    className={cn('mt-1', labelBeside && 'sm:col-start-2')}
                    errors={[error]}
                />
            ) : null}
            {bottomRightLabelComponent ? (
                <div className={cn('mt-1 flex justify-end', labelBeside && 'sm:col-start-2')}>
                    {bottomRightLabelComponent}
                </div>
            ) : null}
            {readonly && !noReadOnlyDivider && !inSection ? <hr className="border-divider mt-2.5 border-t" /> : null}
        </>
    );

    return (
        <CreateDialogField name={name} required={required} isEmpty={isEmpty} hasErrors={hasErrors}>
            <Field
                ref={fieldRef}
                className={cn(
                    'form-field gap-0',
                    // `Field` is `w-full *:w-full` of its own, so opting out has to say so: adding
                    // `w-full` back could never have taken it away.
                    noFullWidthInputWrapper && 'w-auto *:w-auto',
                    // The label's column, then the control's; the label sits level with a one-line control. A phone
                    // keeps the label over its control, as everywhere else.
                    labelBeside && 'grid grid-cols-1 items-start sm:grid-cols-[11rem_minmax(0,1fr)] sm:gap-x-4',
                    isHighlighted && HIGHLIGHT_CLASSES,
                    shouldHide && 'hidden',
                    className
                )}
                data-invalid={hasErrors || undefined}
            >
                {label && (
                    <div
                        className={cn(
                            'flex flex-row items-center justify-between',
                            inSection && FIELD_LABEL_LINE_CLASS,
                            labelBeside && 'sm:min-h-9'
                        )}
                    >
                        <div className={cn('flex gap-1.5', inSection && 'items-center')}>
                            <FieldLabel
                                htmlFor={name}
                                className={cn(
                                    // No flex gap: the asterisk is spaced by its own thin space only.
                                    'text-fg-secondary gap-0 font-normal',
                                    inSection && FIELD_LABEL_CLASS,
                                    hasErrors && 'text-destructive new:text-danger-ink',
                                    isHighlighted && 'text-fg-warning'
                                )}
                            >
                                {shouldTranslateLabel ? t(label) : label}
                                {!readonly && isStarred ? <RequiredAsterisk /> : null}
                            </FieldLabel>
                            {/* In the create dialog a label is one 16px line, which an icon beside it overhangs. */}
                            {tooltip && inCreateDialogSections ? (
                                <span className="flex h-4 items-center">{tooltip}</span>
                            ) : (
                                tooltip
                            )}
                        </div>
                        {inSection && rightLabelComponent ? (
                            <div className={SECTION_RIGHT_LABEL_CLASS}>{rightLabelComponent}</div>
                        ) : (
                            rightLabelComponent
                        )}
                    </div>
                )}
                {inCreateDialogSections ? <div>{control}</div> : control}
            </Field>
        </CreateDialogField>
    );
};

const StarredContext = createContext(false);

/** Stars the label of every field inside, with no rule behind it: for a form that records a blank but reports it. */
export const StarredFields = StarredContext.Provider;

/** The asterisk of a required field's label; the control's own `required` or `aria-required` says it to assistive tech. */
export function RequiredAsterisk() {
    return (
        <span aria-hidden className="text-destructive new:text-danger-ink">
            &thinsp;*
        </span>
    );
}

export default InputWrapper;
